import { CapacityContributionStatus, type MobileCoverageDayRecord } from '@navio/nsdk';

/**
 * Worker shifts the operations calendar counts as working: everything registered for the day, from pending to
 * assigned. The partner calendar and the manager app's calendar list the same people.
 */
export const REGISTERED_SHIFT_STATUS_IDS = [
  CapacityContributionStatus.PENDING,
  CapacityContributionStatus.SUBMITTED,
  CapacityContributionStatus.WAIT_LIST,
  CapacityContributionStatus.RESERVED,
  CapacityContributionStatus.UNASSIGNED,
  CapacityContributionStatus.ASSIGNED
] as const;

export type UtilizationLevel = 'low' | 'medium' | 'high';

/** Utilization below this is headroom: too little sold. */
export const UTILIZATION_LOW_PERCENT = 40;

/** Utilization is a gauge: below 40% is headroom, 70% and up is pressure. Every calendar tints its % by this. */
export const getUtilizationLevel = (percent: number): UtilizationLevel =>
  percent >= 70 ? 'high' : percent >= UTILIZATION_LOW_PERCENT ? 'medium' : 'low';

/**
 * Mobile time customers can still book per API date, summed over each department's coverage days. Each department's
 * workers are their own pool, so an oversold one adds nothing rather than taking time another still has. A day with
 * no mobile time and nothing sold is closed, not full, so it gets no entry instead of a "Full".
 */
export const sumSellableSecondsLeftByDate = (days: MobileCoverageDayRecord[]): Map<string, number> => {
  const secondsLeftByDate = new Map<string, number>();
  for (const day of days) {
    if (day.supplySeconds <= 0 && day.bookingCount === 0) {
      continue;
    }
    secondsLeftByDate.set(day.date, (secondsLeftByDate.get(day.date) ?? 0) + Math.max(0, day.bookableSeconds));
  }
  return secondsLeftByDate;
};
